import { useMemo, useState } from "react";
import { Bot, GitBranch, Loader2, Trash2, User, X } from "lucide-react";
import { createPortal } from "react-dom";
import { Handle, Position } from "reactflow";
import { toast } from "react-toastify";

import { getErrorMessage } from "../../Workspace/services/workspaceService";
import NodeContextMenu from "../controls/NodeContextMenu";
import AddNodeDialog from "../dialogs/AddNodeDialog";
import ConnectNodeDialog from "../dialogs/ConnectNodeDialog";
import workflowService from "../services/workflowService";
import { characterFor } from "src/stage/characters";

// AUTO-CRM-32: automation actions are done by an agent (shown with Appy).
const agent = characterFor("appy");

const TYPE_CONFIG = {
    manual: { icon: User, color: "bg-[#eaf4fa] text-[#0f6392]", dot: "bg-[#147db5]", label: "Manual" },
    automation: { icon: Bot, color: "bg-[#e3eff7] text-[#0f6392]", dot: "bg-[#37a1d2]", label: "Agent" },
    decision: { icon: GitBranch, color: "bg-amber-50 text-amber-700", dot: "bg-amber-500", label: "Decision" },
};

export default function WorkflowNode({
    node,
    selected = false,
    onClick,
    allNodes = [],
    allTransitions = [],
}) {
    const [dialog, setDialog] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const config = TYPE_CONFIG[node?.node_type] ?? TYPE_CONFIG.manual;
    const Icon = config.icon;
    const nodeTransitions = useMemo(
        () => allTransitions.filter((transition) =>
            String(transition.from_node_id) === String(node?.id) ||
            String(transition.to_node_id) === String(node?.id)
        ),
        [allTransitions, node?.id]
    );
    const outgoingTransitions = useMemo(
        () => allTransitions.filter((transition) => String(transition.from_node_id) === String(node?.id)),
        [allTransitions, node?.id]
    );

    if (!node) return null;

    const closeDialog = () => setDialog(null);

    const createConnectedNode = async (data, branch = false) => {
        try {
            const response = await workflowService.createNode({
                stage_id: node.stage_id,
                name: data.name,
                description: data.description || null,
                node_type: data.node_type,
                can_skip: data.can_skip,
                metadata: data.metadata || {},
            }, { refresh: false });
            const newNode = response.data;
            try {
                await workflowService.createTransition({
                    from_node_id: node.id,
                    to_node_id: newNode.id,
                    action_group_name: data.action_group_name || (branch ? "Branch" : null),
                    metadata: {},
                });
            } catch (transitionError) {
                await workflowService.deleteNode(newNode.id).catch(() => {});
                throw transitionError;
            }
            closeDialog();
            toast.success(branch ? "Decision path created" : "Next action created");
        } catch (error) {
            toast.error(getErrorMessage(error, branch ? "Unable to create the decision path." : "Unable to create the next action."));
        }
    };

    const updateNode = async (data) => {
        try {
            await workflowService.updateNode(node.id, {
                name: data.name,
                description: data.description || null,
                node_type: data.node_type,
                can_skip: data.can_skip,
                metadata: data.metadata ?? node.metadata ?? {},
            });
            closeDialog();
            toast.success("Action updated");
        } catch (error) {
            toast.error(getErrorMessage(error, "Unable to update the action."));
        }
    };

    const connectNode = async (data) => {
        try {
            await workflowService.createTransition(data);
            closeDialog();
            toast.success("Actions connected");
        } catch (error) {
            toast.error(getErrorMessage(error, "Unable to connect these actions."));
        }
    };

    const duplicateNode = async () => {
        try {
            await workflowService.createNode({
                stage_id: node.stage_id,
                name: `${node.name} copy`.slice(0, 150),
                description: node.description || null,
                node_type: node.node_type,
                can_skip: Boolean(node.can_skip),
                metadata: node.metadata || {},
            });
            toast.success("Action duplicated");
        } catch (error) {
            toast.error(getErrorMessage(error, "Unable to duplicate the action."));
        }
    };

    const requestDelete = () => {
        setDialog("delete");
    };

    const deleteNode = async () => {
        setDeleting(true);
        try {
            await workflowService.deleteNode(node.id);
            closeDialog();
            toast.success("Action deleted");
        } catch (error) {
            toast.error(getErrorMessage(error, "Unable to delete the action."));
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <div
                onClick={onClick}
                className={`group relative min-w-[280px] cursor-pointer rounded-2xl border bg-white p-4 shadow-[0_7px_20px_rgba(31,78,121,0.08)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(31,78,121,0.13)] ${selected ? "border-[#3ba0d2] ring-4 ring-[#dff3fc]" : "border-[#dce7f1]"}`}
            >
                <Handle type="target" position={Position.Top} style={{ width: 10, height: 10, background: "#ffffff", border: "2px solid #7fb9d8" }} />
                <Handle type="source" position={Position.Bottom} style={{ width: 10, height: 10, background: "#ffffff", border: "2px solid #7fb9d8" }} />

                <div className="flex items-start gap-3">
                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl ${config.color}`}>{node.node_type === "automation" && agent ? <img src={agent.image} alt="" className="h-10 w-10 object-cover" /> : <Icon className="h-[18px] w-[18px]" />}</span>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                                <p className="text-[10px] font-bold uppercase tracking-[0.11em] text-[#8a9ab2]">Action {node.display_order}</p>
                                <h3 className="mt-0.5 truncate text-sm font-bold text-[#173552]">{node.name}</h3>
                            </div>
                            <NodeContextMenu
                                node={node}
                                onAdd={() => setDialog("add")}
                                onBranch={() => setDialog("branch")}
                                onConnect={() => setDialog("connect")}
                                onEdit={() => setDialog("edit")}
                                onDuplicate={duplicateNode}
                                onDelete={requestDelete}
                            />
                        </div>

                        {node.description && <p className="mt-2 line-clamp-2 text-[11px] leading-4 text-[#7185a3]">{node.description}</p>}

                        <div className="mt-3 flex items-center gap-2 border-t border-[#edf2f7] pt-2.5">
                            <span className={`h-1.5 w-1.5 rounded-full ${config.dot}`} />
                            <span className="text-[10px] font-bold text-[#607b9f]">{config.label}</span>
                            {node.can_skip && <span className="rounded-full bg-[#f5f7fa] px-2 py-0.5 text-[9px] font-bold text-[#7185a3]">Skippable</span>}
                            {outgoingTransitions.length > 1 && <span className="ml-auto rounded-full bg-amber-50 px-2 py-0.5 text-[9px] font-bold text-amber-700">{outgoingTransitions.length} paths</span>}
                        </div>
                    </div>
                </div>
            </div>

            <AddNodeDialog open={dialog === "add"} parentNode={node} stageId={node.stage_id} onClose={closeDialog} onSave={(data) => createConnectedNode(data, false)} />
            <AddNodeDialog open={dialog === "edit"} initialData={node} isEdit stageId={node.stage_id} onClose={closeDialog} onSave={updateNode} />
            <AddNodeDialog open={dialog === "branch"} parentNode={node} isBranch stageId={node.stage_id} onClose={closeDialog} onSave={(data) => createConnectedNode(data, true)} />
            <ConnectNodeDialog
                open={dialog === "connect"}
                sourceNode={node}
                availableNodes={allNodes}
                existingTransitions={outgoingTransitions}
                onClose={closeDialog}
                onConnect={connectNode}
            />

            {dialog === "delete" && createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#071321]/45 p-4 backdrop-blur-sm">
                    <div role="alertdialog" aria-modal="true" className="w-full max-w-md rounded-3xl bg-white p-6 shadow-[0_30px_90px_rgba(15,43,92,0.24)]">
                        <div className="flex items-start justify-between gap-4">
                            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-50 text-red-600"><Trash2 className="h-5 w-5" /></span>
                            <button type="button" onClick={closeDialog} aria-label="Close delete confirmation" className="rounded-xl p-2 text-[#7185a3] hover:bg-[#f1f6fb]"><X className="h-4 w-4" /></button>
                        </div>
                        <h2 className="mt-4 text-lg font-bold text-[#102b5c]">Delete “{node.name}”?</h2>
                        <p className="mt-2 text-sm leading-6 text-[#7185a3]">
                            {nodeTransitions.length
                                ? `This removes the action and ${nodeTransitions.length} connected ${nodeTransitions.length === 1 ? "path" : "paths"}. Actions already used by leads cannot be deleted.`
                                : "This removes the action permanently. Actions already used by leads cannot be deleted."}
                        </p>
                        <div className="mt-6 flex justify-end gap-2.5">
                            <button type="button" onClick={closeDialog} disabled={deleting} className="h-10 rounded-xl border border-[#d5e4f2] px-4 text-sm font-bold text-[#607b9f]">Cancel</button>
                            <button type="button" onClick={deleteNode} disabled={deleting} className="inline-flex h-10 items-center gap-2 rounded-xl bg-red-600 px-4 text-sm font-bold text-white disabled:opacity-60">{deleting && <Loader2 className="h-4 w-4 animate-spin" />} Delete action</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}
